/** @jsxImportSource @emotion/react */
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Box from "../components/Box/Box";
import Button from "../components/Button/Button";
import CategoryList from "../components/MainTopicCategory/CategoryList";
import Typography from "../components/Typography/Typography";
import Video from "../components/Video/Video";
import { CategoryContext } from "../contexts/CategoryContext";
import { LanguageContext } from "../contexts/LanguageCtx";
import useBreakpoint from "../hooks/useBreakpoint";
import { icons } from "../theme/icons";
import { images } from "../theme/images";
import { theme } from "../theme/theme";
import { videos } from "../theme/videos";
import {
  MainTopicType,
  reduceCO2Array,
  reduceMaterialsArray,
  reuseCO2Array,
  reuseMaterialsArray,
} from "../types/types";

const StartPage = () => {
  const { selectedLanguage } = useContext(LanguageContext);
  const { setCategory, setMainTopic } = useContext(CategoryContext);
  const [btnContentState, setBtnContentState] = useState({
    isDiscovery: false,
    isVideoOpen: false,
    isVideoZoomIn: false,
  });
  const [mainTopicOnOverEffect, setMainTopicOnOverEffect] = useState<MainTopicType | null>(null);
  const { isMobile, isTablet, isLandscape, isDesktop, isMobileLandscape } = useBreakpoint();
  const [fadeInAnimation, setFadeInAnimation] = useState(false);
  const { SelectIcon } = icons.general;
  const { Wheel } = images;

  useEffect(() => setFadeInAnimation(true), []);

  return (
     <Box
      display="flex"
      flexDirection={isLandscape || isTablet || isMobile ? "column" : undefined}
      justifyContent="space-between"
      maxHeight="100vh"
      overflowX="hidden"
      opacity={fadeInAnimation ? 1 : 0}
      transition="all 1s ease-out"
    >
      <Box 
        maxWidth={isLandscape || isTablet ? "60%" : 550}
        backgroundColor={theme.colors.background}
        paddingTop={isMobile ? "5vh" : isTablet ? "10vh" : "15vh"}
        paddingLeft={isMobile ? 30 : 65}
        paddingRight={isMobile ? 30 : undefined}
      >
        <Typography variant={isMobile ? "h1_mobile" : "h1"} color="primary">
          {t(`heidelberg.headline.title`)}
        </Typography>

        <Typography
          variant={isMobile ? "h2_mobile" : "h2"}
          color="primary"
          maxWidth={isMobile ? "80%" : undefined}
          marginTop={20}
          width={isLandscape || isTablet ? 500 : undefined}
        >
          {t(`heidelberg.headline.description`)}
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          marginTop={isMobile ? 30 : 50}
          marginBottom={24}
        >
          <Typography
            variant={isMobile ? "body1_mobile" : "body1"}
            color="primary"
            marginBottom={12}
          >
            {t(`heidelberg.category.sustainabilityCommitments2030.title`)}
          </Typography>
          <Box display="flex" width={320} justifyContent="space-between"  zIndex={1}>
            <Button
              label={t(`heidelberg.buttons.discover`)}
              onClick={() => {
                setBtnContentState({
                  ...btnContentState,
                  isDiscovery: true,
                  isVideoOpen: false,
                });
                window.open(
                  `https://www.heidelbergmaterials.com/${selectedLanguage === "en" ? "en" : "de"}/sustainability-commitments-2030`,
                  "_blank"
                );
              }}
              type="discover"
            />
            <Button
              label={t(`heidelberg.buttons.play`)}
              onClick={() =>
                setBtnContentState({
                  isVideoZoomIn: false,
                  isDiscovery: false,
                  isVideoOpen: true,
                })
              }
              type="play"
              style={{display: 'none'}}
            />
          </Box>
        </Box>

        <Video
          isVideoOpen={btnContentState.isVideoOpen}
          isVideoZoomIn={btnContentState.isVideoZoomIn}
          handleZoomIn={() =>
            setBtnContentState({ ...btnContentState, isVideoZoomIn: true })
          }
          handleClose={() =>
            setBtnContentState({ ...btnContentState, isVideoOpen: false })
          }
          videoUrl={
            selectedLanguage === "de"
              ? videos.SustainabilityCommitments2030_DE
              : videos.SustainabilityCommitments2030_EN
          }
        />
      </Box>

      <Box 
        width={isLandscape || isTablet || isMobile ? "100%" : "75%"}
        height={"100vh"}
        display="flex"
        justifyContent="center"
        alignItems="center"
        backgroundColor={theme.colors.background}
        paddingLeft={isDesktop ? 75 : undefined}
        paddingBottom={isTablet || isLandscape ? 100 : undefined}
        opacity={!isDesktop && btnContentState.isVideoOpen ? 0 : 1}
        position="relative"
      >
        {isMobile && (
          <Box position="absolute" top={0} right={40}>
            <Box display="flex" justifyContent="center" width={130}>
              <Typography variant="caption" color="primary">
                {t("heidelberg.selectText")}
              </Typography>
              <SelectIcon css={{paddingBottom: 10, width: 44, height: 44 }} />
            </Box>
          </Box>
        )}

        <Box
          width="80%"
          height="auto"
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginRight={isDesktop ? 100 : undefined}
        >
          <img
            src={Wheel}
            alt="wheel"
            css={{
              width: isMobile ? 450 : "80%",
              height: "100%",
              objectFit: "cover",
              opacity: fadeInAnimation ? 1 : 0,
              transition: "all 1s ease-out",
            }}
            // onLoad={()=> setImageLoaded(true)}
          />
          {isMobile && (
            <Box
              marginTop={isMobileLandscape? 60: 0}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              display="flex"
              flexWrap="wrap"
            >
              <Box
                width="50%"
                height="50%"
                onClick={() => {
                  setCategory("DecarbonisedProducts");
                  setMainTopic("reuseCO2");
                }}
              ></Box>
              <Box
                width="50%"
                height="50%"
                onClick={() => {
                  setCategory("ProductsUsingLessMaterial");
                  setMainTopic("reduceMaterials");
                }}
              ></Box>
              <Box
                width="50%"
                height="50%"
                onClick={() => {
                  setCategory("CircularProducts");
                  setMainTopic("reuseMaterials");
                }}
              ></Box>
              <Box
                width="50%"
                height="50%"
                onClick={() => {
                  setCategory("CarbonReducedProducts");
                  setMainTopic("reduceCO2");
                }}
              ></Box>
            </Box>
          )}
          {!isMobile && (
            <Box>
              <CategoryList
                topic="reuseCO2"
                categoryByMainTopicArray={reuseCO2Array}
                onMouseOver={() => setMainTopicOnOverEffect("reuseCO2")}
                onMouseOut={() => setMainTopicOnOverEffect(null)}
                mainTopicOnOverEffect={mainTopicOnOverEffect}
              />
              <CategoryList
                topic="reduceMaterials"
                categoryByMainTopicArray={reduceMaterialsArray}
                onMouseOver={() => setMainTopicOnOverEffect("reduceMaterials")}
                onMouseOut={() => setMainTopicOnOverEffect(null)}
                mainTopicOnOverEffect={mainTopicOnOverEffect}
              />
              <CategoryList
                topic="reduceCO2"
                categoryByMainTopicArray={reduceCO2Array}
                onMouseOver={() => setMainTopicOnOverEffect("reduceCO2")}
                onMouseOut={() => setMainTopicOnOverEffect(null)}
                mainTopicOnOverEffect={mainTopicOnOverEffect}
              />
              <CategoryList
                topic="reuseMaterials"
                categoryByMainTopicArray={reuseMaterialsArray}
                onMouseOver={() => setMainTopicOnOverEffect("reuseMaterials")}
                onMouseOut={() => setMainTopicOnOverEffect(null)}
                mainTopicOnOverEffect={mainTopicOnOverEffect}
              />
            </Box>
          )}
        </Box>
      </Box>
      
     </Box>
  );
};

export default StartPage;
